import type { Pos } from '@color-dots/game';
import type { CanvasRenderer } from '../game/CanvasRenderer.js';
import type { Scene3DApp } from '../scene3d/Scene3DApp.js';
import { gridToWorld } from '../scene3d/Grid3DLayout.js';
import type { WordLabel } from './types.js';
import * as THREE from 'three';

const CELL_FILL = 0.86;

export class WordLabelsLayer {
  private container: HTMLElement;
  private labelEls = new Map<string, HTMLElement>();
  private labels = new Map<string, WordLabel>();
  private mode: '2d' | '3d';
  private canvasRenderer: CanvasRenderer | null = null;
  private scene3d: Scene3DApp | null = null;
  private canvas: HTMLCanvasElement;
  private boardSize = 5;

  constructor(
    parent: HTMLElement,
    canvas: HTMLCanvasElement,
    mode: '2d' | '3d',
  ) {
    this.canvas = canvas;
    this.mode = mode;
    this.container = document.createElement('div');
    this.container.className = 'word-labels-layer';
    this.container.setAttribute('aria-hidden', 'true');
    parent.appendChild(this.container);
  }

  attach2d(renderer: CanvasRenderer): void {
    this.canvasRenderer = renderer;
    this.scene3d = null;
    this.mode = '2d';
  }

  attach3d(scene: Scene3DApp): void {
    this.scene3d = scene;
    this.canvasRenderer = null;
    this.mode = '3d';
  }

  setLabels(labels: Map<string, WordLabel>, boardSize: number): void {
    this.labels = labels;
    this.boardSize = boardSize;
    this.syncElements();
  }

  update(): void {
    if (this.mode === '2d') {
      this.update2d();
    } else {
      this.update3d();
    }
  }

  private syncElements(): void {
    const needed = new Set<string>();

    for (const [key, label] of this.labels) {
      needed.add(key);
      let el = this.labelEls.get(key);
      if (!el) {
        el = document.createElement('span');
        el.className = 'word-label';
        this.container.appendChild(el);
        this.labelEls.set(key, el);
      }
      el.textContent = label.text;
      el.className = `word-label word-label--${label.role}`;
      el.dataset.pos = key;
    }

    for (const [key, el] of this.labelEls) {
      if (!needed.has(key)) {
        el.remove();
        this.labelEls.delete(key);
      }
    }

    this.update();
  }

  private parsePos(key: string): Pos {
    const [x, y] = key.split(',').map(Number);
    return [x, y];
  }

  private applyCellBox(el: HTMLElement, cellPx: number): void {
    const box = cellPx * CELL_FILL;
    el.style.width = `${box}px`;
    el.style.height = `${box}px`;
    el.style.fontSize = `${Math.max(11, Math.min(15, cellPx * 0.2))}px`;
  }

  private update2d(): void {
    if (!this.canvasRenderer) return;

    const cellPx = this.canvasRenderer.getCellSize();
    if (cellPx <= 0) return;

    const canvasRect = this.canvas.getBoundingClientRect();
    const parentRect = this.container.parentElement?.getBoundingClientRect();
    if (!parentRect) return;

    const offsetX = canvasRect.left - parentRect.left;
    const offsetY = canvasRect.top - parentRect.top;

    for (const [, el] of this.labelEls) {
      const pos = this.parsePos(el.dataset.pos!);
      const { x, y } = this.canvasRenderer.posToPixel(pos);
      el.style.left = `${offsetX + x}px`;
      el.style.top = `${offsetY + y}px`;
      this.applyCellBox(el, cellPx);
    }
  }

  private getProjectedCellSize(
    camera: THREE.PerspectiveCamera,
    canvasRect: DOMRect,
    offsetX: number,
    offsetY: number,
  ): number {
    const vec = new THREE.Vector3();
    const project = (col: number, row: number) => {
      const world = gridToWorld(col, row, this.boardSize);
      vec.copy(world);
      this.scene3d!.boardGroup.localToWorld(vec);
      vec.project(camera);
      return {
        x: (vec.x * 0.5 + 0.5) * canvasRect.width + offsetX,
        y: (-vec.y * 0.5 + 0.5) * canvasRect.height + offsetY,
      };
    };

    const a = project(0, 0);
    const b = project(1, 0);
    return Math.hypot(b.x - a.x, b.y - a.y);
  }

  private update3d(): void {
    if (!this.scene3d) return;

    const camera = this.scene3d.getCamera();
    const canvasRect = this.canvas.getBoundingClientRect();
    const parentRect = this.container.parentElement?.getBoundingClientRect();
    if (!parentRect) return;

    const offsetX = canvasRect.left - parentRect.left;
    const offsetY = canvasRect.top - parentRect.top;
    const w = canvasRect.width;
    const h = canvasRect.height;
    const cellPx = this.getProjectedCellSize(camera, canvasRect, offsetX, offsetY);

    const vec = new THREE.Vector3();

    for (const [, el] of this.labelEls) {
      const pos = this.parsePos(el.dataset.pos!);
      const world = gridToWorld(pos[0], pos[1], this.boardSize);
      world.y += 0.12;
      vec.copy(world);
      this.scene3d.boardGroup.localToWorld(vec);
      vec.project(camera);

      const sx = (vec.x * 0.5 + 0.5) * w + offsetX;
      const sy = (-vec.y * 0.5 + 0.5) * h + offsetY;
      const visible = vec.z < 1;

      el.style.left = `${sx}px`;
      el.style.top = `${sy}px`;
      el.style.opacity = visible ? '1' : '0.3';
      if (cellPx > 0) {
        this.applyCellBox(el, cellPx);
      }
    }
  }

  destroy(): void {
    this.container.remove();
    this.labelEls.clear();
  }
}
